import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Typography,
  Alert,
  Spin,
  Tag,
  Tooltip,
  Row,
  Col,
} from 'antd';
import { PlusOutlined, EditOutlined, WarningOutlined } from '@ant-design/icons';
import { useCategories } from '../Hooks/Courses/useCategories';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const { Text } = Typography;
const { TextArea } = Input;

const CategoriesList = () => {
  const navigate = useNavigate();
  const [subCategoriesModalVisible, setSubCategoriesModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [editingSubCategories, setEditingSubCategories] = useState([]);
  const [newSubCategoryInput, setNewSubCategoryInput] = useState('');

  const {
    categories,
    error,
    loading,
    modalVisible,
    editingCategory,
    form,
    pagination, 
    handlePageChange,
    handleSubmit,
    handleEdit,
    handleModalClose,
    handleAddNew,
    fetchCategoryForUpdate,
    updateSubCategories,
    loadingSubCategories,
    fetchCategories,
  } = useCategories();

  const handleOpenSubCategoriesModal = async (category) => {
    setSelectedCategory(category);

    try {
      const data = await fetchCategoryForUpdate(category.id);
      const subCategoriesList = data.subCategories || [];
      setEditingSubCategories(subCategoriesList);
      setNewSubCategoryInput('');
      setSubCategoriesModalVisible(true);
    } catch (error) {
      console.error('Error fetching category details:', error);
      toast.error(error || 'Failed to load subcategories');
    }
  };

  const handleAddNewSubCategory = () => {
    const name = newSubCategoryInput.trim();
    if (!name) {
      toast.warning('Please enter a subcategory name');
      return;
    }

    if (editingSubCategories.some(sub => sub.name.toLowerCase() === name.toLowerCase())) {
      toast.warning('This subcategory already exists');
      return;
    }

    setEditingSubCategories(prev => [
      ...prev,
      { name, id: null, isUsed: false }
    ]);

    setNewSubCategoryInput('');
  };

  const handleRemoveSubCategory = (index) => {
    const subCategory = editingSubCategories[index];
    if (subCategory.isUsed) {
      toast.warning('This subcategory is in use and cannot be removed');
      return;
    }
    setEditingSubCategories(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubCategoriesSubmit = async () => {
    try {
      const activeSubCategories = editingSubCategories.filter(sub => !sub._removed);
      const subCategoryNames = activeSubCategories.map(sub => sub.name);

      if (subCategoryNames.length === 0) {
        toast.error('Please add at least one subcategory');
        return;
      }

      await updateSubCategories(selectedCategory.id, {
        parentCategoryId: selectedCategory.id,
        names: subCategoryNames
      });

      setSubCategoriesModalVisible(false);
      setEditingSubCategories([]);
      setNewSubCategoryInput('');
      await fetchCategories();
    } catch (error) {
      console.error('Error updating subcategories:', error);
    }
  };

  const toggleExpanded = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const columns = [
    {
      title: 'Category Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          {record.description && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.description.length > 50
                ? `${record.description.substring(0, 50)}...`
                : record.description}
            </Text>
          )}
        </Space>
      ),
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
    },
    {
      title: 'Subcategories',
      dataIndex: 'subCategories',
      key: 'subCategories',
      render: (subCategoriesList, record) => {
        const hasSubCategories = subCategoriesList && subCategoriesList.length > 0;
        if (!hasSubCategories) {
          return (
            <Space wrap>
              <Tooltip title="This category has no subcategories">
                <Text type="warning" style={{ color: '#faad14' }}>
                  <WarningOutlined /> No subcategories
                </Text>
              </Tooltip>
              <Button
                type="link"
                size="small"
                onClick={() => handleOpenSubCategoriesModal(record)}
              >
                Add
              </Button>
            </Space>
          );
        }

        const displayLimit = 3;
        const shouldShowMore = subCategoriesList.length > displayLimit;
        const isExpanded = expandedCategories[record.id];
        const displaySubCategories = isExpanded
          ? subCategoriesList
          : subCategoriesList.slice(0, displayLimit);

        return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Space wrap>
              {displaySubCategories.map((sub) => (
                <Tag
                  key={sub.id}
                  color={sub.isUsed ? "green" : "blue"}
                  style={{ marginBottom: 4 }}
                >
                  {sub.name}
                </Tag>
              ))}
            </Space>
            {shouldShowMore && (
              <Button
                type="link"
                size="small"
                onClick={() => toggleExpanded(record.id)}
                style={{ paddingLeft: 0 }}
              >
                {isExpanded ? 'Show Less' : `+${subCategoriesList.length - displayLimit} more`}
              </Button>
            )}
          </Space>
        );
      },
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (description) => (
        <Text type="secondary">
          {description && description.length > 100
            ? `${description.substring(0, 100)}...`
            : description || 'No description'}
        </Text>
      ),
      responsive: ['md', 'lg', 'xl'],
    },
    {
      title: 'Created On',
      dataIndex: 'createdOn',
      key: 'createdOn',
      render: (date) => date ? new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }) : 'N/A',
      responsive: ['sm', 'md', 'lg', 'xl'],
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space wrap>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            onClick={() => handleOpenSubCategoriesModal(record)}
          >
            Manage Subcategories
          </Button>
        </Space>
      ),
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Categories"
        description={error}
        type="error"
        showIcon
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        }
      />
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <Card
        className="card-professional"
        title="Course Categories"
        extra={
          <Space wrap>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNew}
            >
              Add Category
            </Button>
            <Button
              type="default"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </Space>
        }
      >
        <Alert
          message="No Categories Found"
          description="Start by adding your first course category."
          type="info"
          showIcon
          action={
            <Button size="small" type="primary" onClick={handleAddNew}>
              Add Category
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <Card
      className="card-professional"
      title="Course Categories"
      extra={
        <Space wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddNew}
          >
            Add Category
          </Button>
          <Button
            type="default"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </Space>
      }
      bodyStyle={{ padding: 0 }}
    >
      <div style={{ overflowX: 'auto' }}>
        <Table
          columns={columns}
          dataSource={categories}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.currentPage,
            pageSize: pagination.pageSize,
            total: pagination.totalCount,
            showSizeChanger: true,
            onChange: handlePageChange,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} categories`
          }}
        />
      </div>

      {/* Category Modal */}
      <Modal
        title={editingCategory ? 'Edit Category' : 'Add New Category'}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        destroyOnClose
        width="90%"
        style={{ maxWidth: 600 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            name="name"
            label="Category Name"
            rules={[
              { required: true, message: 'Please enter category name' },
              { min: 2, message: 'Category name must be at least 2 characters' },
              { max: 100, message: 'Category name cannot exceed 100 characters' }
            ]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { min: 10, message: 'Description must be at least 10 characters' },
              { max: 500, message: 'Description cannot exceed 500 characters' }
            ]}
          >
            <TextArea
              placeholder="Enter category description"
              rows={4}
              showCount
              maxLength={500}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space wrap>
              <Button onClick={handleModalClose}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {editingCategory ? 'Update Category' : 'Add Category'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Subcategories Modal */}
      <Modal
        title={`Manage Subcategories for "${selectedCategory?.name || ''}"`}
        open={subCategoriesModalVisible}
        onCancel={() => {
          setSubCategoriesModalVisible(false);
          setEditingSubCategories([]);
          setNewSubCategoryInput('');
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setSubCategoriesModalVisible(false);
              setEditingSubCategories([]);
              setNewSubCategoryInput('');
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleSubCategoriesSubmit}
            loading={loadingSubCategories}
          >
            Save Subcategories
          </Button>,
        ]}
        destroyOnClose
        width="90%"
        style={{ maxWidth: 600 }}
      >
        {/* Existing Subcategories */}
        <div style={{ marginBottom: 16 }}>
          <Text strong>Current Subcategories:</Text>
          {editingSubCategories.length > 0 ? (
            <div style={{ marginTop: 8 }}>
              <Space wrap>
                {editingSubCategories.map((sub, index) => (
                  <Tag
                    key={sub.id || `new-${index}`}
                    closable={!sub.isUsed}
                    onClose={() => handleRemoveSubCategory(index)}
                    color={sub.isUsed ? "green" : "blue"}
                    style={{ marginBottom: 4, padding: '4px 8px' }}
                  >
                    {sub.name}
                  </Tag>
                ))}
                {editingSubCategories.some(sub => sub.isUsed) && (
                  <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
                    <WarningOutlined /> Green tags are in use and cannot be removed
                  </div>
                )}
              </Space>
            </div>
          ) : (
            <div style={{ marginTop: 8, color: '#999' }}>
              No subcategories yet. Add your first one below.
            </div>
          )}
        </div>

        {/* Add New Subcategory */}
        <div style={{ marginBottom: 16 }}>
          <Text strong>Add New Subcategory:</Text>
          <Space.Compact style={{ width: '100%', marginTop: 8 }}>
            <Input
              value={newSubCategoryInput}
              onChange={(e) => setNewSubCategoryInput(e.target.value)}
              placeholder="Enter new subcategory name"
              onPressEnter={handleAddNewSubCategory}
            />
            <Button
              type="primary"
              onClick={handleAddNewSubCategory}
              icon={<PlusOutlined />}
            >
              Add
            </Button>
          </Space.Compact>
        </div>
      </Modal>
    </Card>
  );
};

export default CategoriesList;
